import React, { useEffect, useState } from 'react';
import { LABEL_COLORS, LABEL_ICONS, updateLabel } from '../../utils/labelApi';
import { LabelIcon } from './LabelIcons';
import './labels.css';

const EditLabelModal = ({ isOpen, label, onClose, onUpdated }) => {
  const [name, setName] = useState('');
  const [color, setColor] = useState(LABEL_COLORS[0]);
  const [icon, setIcon] = useState(LABEL_ICONS[0]);
  const [visible, setVisible] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen || !label) return;
    setName(label.name || '');
    setColor(label.color || LABEL_COLORS[0]);
    setIcon(label.icon || LABEL_ICONS[0]);
    setVisible(label.visible !== false);
    setError('');
    setSaving(false);
  }, [isOpen, label]);

  if (!isOpen || !label) return null;

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Label name is required');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const response = await updateLabel(label._id, {
        name: trimmed,
        color,
        icon,
        visible,
      });
      if (response.success) {
        onUpdated?.(response.data);
        onClose?.();
      } else {
        setError(response.message || 'Could not update label');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Could not update label');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="label-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="label-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-label-title"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id="edit-label-title">Edit label</h2>
        <label className="label-modal-field">
          <span>Label name</span>
          <input
            type="text"
            value={name}
            maxLength={30}
            autoFocus
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') handleSave();
              if (event.key === 'Escape') onClose?.();
            }}
          />
        </label>
        <div className="label-modal-field">
          <span>Color</span>
          <div className="label-color-grid">
            {LABEL_COLORS.map((swatch) => (
              <button
                key={swatch}
                type="button"
                className={`label-color-swatch${color === swatch ? ' is-selected' : ''}`}
                style={{ background: swatch }}
                onClick={() => setColor(swatch)}
              />
            ))}
          </div>
        </div>
        <div className="label-modal-field">
          <span>Icon</span>
          <div className="label-icon-grid">
            {LABEL_ICONS.map((iconName) => (
              <button
                key={iconName}
                type="button"
                className={`label-icon-swatch${icon === iconName ? ' is-selected' : ''}`}
                onClick={() => setIcon(iconName)}
              >
                <LabelIcon name={iconName} size={18} color={color} />
              </button>
            ))}
          </div>
        </div>
        <label className="label-modal-field" style={{ gridTemplateColumns: 'auto 1fr', alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={visible}
            onChange={(event) => setVisible(event.target.checked)}
          />
          <span>Show in sidebar</span>
        </label>
        {error && <div className="label-modal-error">{error}</div>}
        <div className="label-modal-actions">
          <button type="button" className="label-modal-cancel" onClick={onClose}>Cancel</button>
          <button type="button" className="label-modal-save" onClick={handleSave} disabled={saving || !name.trim()}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditLabelModal;
